/**
 * Priority-shift groups for the Hybrid drift queue.
 *
 * Ordered Exchange rules (transport rules and the other Priority-ordered
 * rules) carry their place in the list. Adding or moving one rule shifts every
 * rule below it by one, and the continuous watch reports each shifted rule as
 * its own drift, so one change can fill the queue with rows like
 * "Priority 37 → 38". The server sets `priorityShift` on a row whose only
 * change is its Priority; this module folds two or more rows of one type that
 * moved by the same amount into one collapsed group with one line of copy.
 */

import { driftResourceName } from '@/lib/drift-names'
import type { TcmDriftSummary } from '@/types/tcm'
import type { UnifiedDriftRow } from '@/types/unifiedDrift'

const GROUP_PREFIX = 'TCM-PRIORITY:'

function shiftOf(row: UnifiedDriftRow): number | null {
  if (row.source !== 'TCM') return null
  const shift = (row.raw as TcmDriftSummary).priorityShift
  return typeof shift === 'number' && shift !== 0 ? shift : null
}

/**
 * Row id → group key, for each row that shares its type and shift with at
 * least one other row in `rows`. A lone shifted row stays a plain row: one
 * rule's priority changing is its own change.
 */
export function priorityShiftGroups(rows: UnifiedDriftRow[]): Map<string, string> {
  const keyOf = (row: UnifiedDriftRow) => {
    const shift = shiftOf(row)
    return shift === null ? null : `${GROUP_PREFIX}${row.resourceType}:${shift}`
  }
  const counts = new Map<string, number>()
  for (const row of rows) {
    const key = keyOf(row)
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const out = new Map<string, string>()
  for (const row of rows) {
    const key = keyOf(row)
    if (key && (counts.get(key) ?? 0) >= 2) out.set(row.id, key)
  }
  return out
}

/** "Exchange Transport Rule" → "Exchange Transport Rules"; "… Policy" → "… Policies". */
function pluralName(name: string): string {
  if (/[^aeiou]y$/i.test(name)) return `${name.slice(0, -1)}ies`
  if (/s$/i.test(name)) return name
  return `${name}s`
}

function places(n: number): string {
  return n === 1 ? 'one place' : `${n} places`
}

/**
 * The group's copy. `title` names the rules and the move; `note` says what
 * caused it and, when the reader can accept them (`actionable`), what to do;
 * `label` names the group in its controls ("Expand …", "Select all in …").
 */
export function priorityShiftCopy(
  rows: UnifiedDriftRow[],
  { actionable }: { actionable: boolean },
): { title: string; note: string; label: string } {
  const first = rows[0]
  const shift = shiftOf(first) ?? 0
  const name = pluralName(driftResourceName(first.resourceType))
  const move = shift > 0 ? `moved down ${places(shift)}` : `moved up ${places(-shift)}`
  const cause =
    shift > 0 ? 'A rule was added or moved above them.' : 'A rule above them was removed or moved below them.'
  const action = shift > 0 ? 'Check that rule, then accept these together.' : 'Check that change, then accept these together.'
  return {
    title: `${rows.length} ${name} ${move}`,
    note: actionable ? `${cause} ${action}` : cause,
    label: `${name} that ${move}`,
  }
}
